import { Menu, Transition } from '@headlessui/react';
import {
  CameraIcon,
  CollectionIcon,
  CubeIcon,
  FilmIcon,
  LightBulbIcon,
  MusicNoteIcon,
  PencilIcon,
  PhotographIcon,
  PlusIcon,
  StarIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import {
  CheckCircleIcon,
  DotsVerticalIcon,
  TrashIcon,
} from '@heroicons/react/solid';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';
import uuidv4 from '@/lib/uuid';

import Modal from '@/components/atoms/Modal';
import Button from '@/components/buttons/Button';
import EditModal from '@/components/images/EditModal';
import Spinner from '@/components/Spinner';
import {
  ACCEPTED_AUDIO,
  decodeReferenceAudioFile,
  ReferenceAudioCard,
  ReferenceAudioValue,
  validateReferenceAudioFile,
} from '@/components/videos/ReferenceAudioCard';

import type { CinematicOption } from '@/constants/cinematicShot';
import {
  APERTURE_OPTIONS,
  CAMERA_OPTIONS,
  CAMERA_PRESETS,
  findMatchingPreset,
  FIXED_OPTICS_CAMERAS,
  FOCAL_LENGTH_OPTIONS,
  LENS_OPTIONS,
  LIGHTING_OPTIONS,
  SHOT_TYPE_OPTIONS,
} from '@/constants/cinematicShot';
import { STYLE_OPTIONS } from '@/constants/imageStyles';
import { compressImage } from '@/utils/compressImage';
import axiosInstance from '@/utils/httpClient';
import { createClient as createComponentClient } from '@/utils/supabase/component';
import { uploadUserImage } from '@/utils/uploadUserImage';
import { useUser } from '@/utils/useUser';

import type { Actor } from '@/types/actor';
import type { UserUpload, UserUploadKind } from '@/types/user_upload';

export type SceneTab =
  | 'all'
  | 'character'
  | 'object'
  | 'history'
  | 'video'
  | 'audio'
  | 'style'
  | 'camera'
  | 'lighting'
  | 'shot-type';

const SHOT_GROUPS = [
  { name: 'Extreme Close-Up', prefix: 'Extreme Close-Up' },
  { name: 'Close-Up', prefix: 'Close-Up' },
  { name: 'Medium Shot', prefix: 'Medium Shot' },
  { name: 'Three Quarter', prefix: 'Three Quarter' },
  { name: 'Long Shot', prefix: 'Long Shot' },
  { name: 'Wide Shot', prefix: 'Wide Shot' },
];

interface SceneValues {
  style: string;
  camera: string;
  lens: string;
  focalLength: string;
  aperture: string;
  lighting: string;
  shotType: string;
}

interface SceneModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: SceneTab;
  /** Omit when the modal is opened for its library tabs only. */
  values?: SceneValues;
  selectedCharacters?: string[];
  selectedObjects?: string[];
  selectedImages?: string[];
  /** Tabs to hide (e.g. ['style'] on the Video Generator). */
  hiddenTabs?: SceneTab[];
  /** Show the user's custom (created) actors as pickable characters. */
  showCustomActors?: boolean;
  /**
   * Single-image pick mode: clicking any image immediately calls onPick with
   * its URL (no multi-select). Only the library tabs are shown and the footer
   * is hidden. Used by the Video Generator for the Start/End frame cards.
   */
  onPick?: (url: string) => void;
  onApply?: (values: SceneValues) => void;
  onLibrarySelectionApply?: (selection: {
    characters: string[];
    objects: string[];
    images: string[];
    /** Reference video URL ('' when none). Only set when showVideoTab is on. */
    video?: string;
    /** Reference audio (null when none). Only set when showAudioTab is on. */
    audio?: ReferenceAudioValue | null;
  }) => void;
  /**
   * Opt-in Video library tab for models that accept a reference video
   * (Gemini Omni Flash video editing on the Video Generator). Off by default
   * so the AI Images page is unaffected.
   */
  showVideoTab?: boolean;
  /** Currently applied reference video URL (used to seed the Video tab). */
  selectedVideo?: string;
  /**
   * Opt-in Audio tab for models that accept reference audio (Seedance 2.0 /
   * MiniMax H3 on the Video Generator). Off by default so the AI Images page
   * is unaffected.
   */
  showAudioTab?: boolean;
  /** Currently applied reference audio (used to seed the Audio tab). */
  selectedAudio?: ReferenceAudioValue | null;
  /** Combined reference-audio length cap in seconds (drives the trimmer). */
  audioMaxSeconds?: number;
  /**
   * Reference-video limits for the model that will receive the clip. They vary
   * a lot — Gemini Omni Flash edits a 4-20s clip inline, Seedance 2.0 and
   * MiniMax H3 take 2-15s motion references by URL, Seedance 2.5 stretches to
   * 30s — so the picker must reject at the target's limits rather than one
   * hardcoded set. /api/generate-video re-checks all of it from the file.
   */
  videoLimits?: ReferenceVideoLimits;
}

export interface ReferenceVideoLimits {
  minSeconds: number;
  maxSeconds: number;
  maxBytes: number;
  /** The clip is the footage being edited, not a motion reference. */
  isEditSource?: boolean;
}

const NO_SCENE_VALUES: SceneValues = {
  style: 'None',
  camera: 'Standard',
  lens: 'Standard',
  focalLength: 'Standard',
  aperture: 'Standard',
  lighting: 'None',
  shotType: 'None',
};

/**
 * Gemini Omni Flash's limits, kept as the default so the AI Images page and
 * any caller that doesn't pass `videoLimits` behaves exactly as before.
 */
const DEFAULT_REFERENCE_VIDEO_LIMITS: ReferenceVideoLimits = {
  minSeconds: 4,
  maxSeconds: 20,
  maxBytes: 20 * 1024 * 1024,
  isEditSource: true,
};

function SceneModal({
  isOpen,
  onClose,
  initialTab = 'style',
  values = NO_SCENE_VALUES,
  selectedCharacters = [],
  selectedObjects = [],
  selectedImages = [],
  hiddenTabs = [],
  showCustomActors = false,
  onPick,
  onApply,
  onLibrarySelectionApply,
  showVideoTab = false,
  selectedVideo = '',
  showAudioTab = false,
  selectedAudio = null,
  audioMaxSeconds = 15,
  videoLimits = DEFAULT_REFERENCE_VIDEO_LIMITS,
}: SceneModalProps) {
  const pickMode = !!onPick;
  const { t } = useLocale('ai-images');
  const [tab, setTab] = useState<SceneTab>(initialTab);

  const [style, setStyle] = useState(values.style);
  const [camera, setCamera] = useState(values.camera);
  const [lens, setLens] = useState(values.lens);
  const [focalLength, setFocalLength] = useState(values.focalLength);
  const [aperture, setAperture] = useState(values.aperture);
  const [lighting, setLighting] = useState(values.lighting);
  const [shotType, setShotType] = useState(values.shotType);

  const [styleSearch, setStyleSearch] = useState('');
  const [lightingSearch, setLightingSearch] = useState('');
  const [cameraMode, setCameraMode] = useState<'presets' | 'advanced'>(
    'presets',
  );
  const [characterSelection, setCharacterSelection] =
    useState<string[]>(selectedCharacters);
  const [objectSelection, setObjectSelection] =
    useState<string[]>(selectedObjects);
  const [imageSelection, setImageSelection] =
    useState<string[]>(selectedImages);
  const [videoSelection, setVideoSelection] = useState<string>(selectedVideo);
  const [audioSelection, setAudioSelection] =
    useState<ReferenceAudioValue | null>(selectedAudio);
  // URLs of generated images currently loaded in the History tab. Uploads and
  // generated images share the imageSelection bucket, so we track which of
  // those URLs are generated to scope the History tab's clear/count to them.
  const [historyUrls, setHistoryUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setStyle(values.style);
    setCamera(values.camera);
    setLens(values.lens);
    setFocalLength(values.focalLength);
    setAperture(values.aperture);
    setLighting(values.lighting);
    setShotType(values.shotType);
    setTab(initialTab);
    setStyleSearch('');
    setLightingSearch('');
    setCameraMode('presets');
    setCharacterSelection(selectedCharacters);
    setObjectSelection(selectedObjects);
    setImageSelection(selectedImages);
    setVideoSelection(selectedVideo);
    setAudioSelection(selectedAudio);
    setHistoryUrls([]);
    // selectedCharacters / selectedObjects / selectedImages intentionally omitted from deps
    // to avoid resetting selection while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    initialTab,
    values.style,
    values.camera,
    values.lens,
    values.focalLength,
    values.aperture,
    values.lighting,
    values.shotType,
  ]);

  const isFixedOptics = FIXED_OPTICS_CAMERAS.has(camera);
  const matchingPreset = findMatchingPreset(
    camera,
    lens,
    focalLength,
    aperture,
  );

  const totalLibrarySelected =
    characterSelection.length + objectSelection.length + imageSelection.length;

  const historyUrlSet = useMemo(() => new Set(historyUrls), [historyUrls]);
  const historySelectedCount = imageSelection.filter((u) =>
    historyUrlSet.has(u),
  ).length;

  const summary: Record<SceneTab, string | null> = {
    all:
      totalLibrarySelected > 0
        ? `${totalLibrarySelected} ${t('selected')}`
        : null,
    character:
      characterSelection.length > 0
        ? `${characterSelection.length} ${t('selected')}`
        : null,
    object:
      objectSelection.length > 0
        ? `${objectSelection.length} ${t('selected')}`
        : null,
    history:
      historySelectedCount > 0
        ? `${historySelectedCount} ${t('selected')}`
        : null,
    video: videoSelection ? `1 ${t('selected')}` : null,
    audio: audioSelection ? `1 ${t('selected')}` : null,
    style: style !== 'None' ? style : null,
    camera: matchingPreset?.label ?? (camera !== 'Standard' ? camera : null),
    lighting: lighting !== 'None' ? lighting : null,
    'shot-type': shotType !== 'None' ? shotType : null,
  };

  // In pick mode only the library tabs make sense (pick a single image).
  const effectiveHiddenTabs: SceneTab[] = pickMode
    ? Array.from(
        new Set<SceneTab>([
          ...hiddenTabs,
          'style',
          'camera',
          'lighting',
          'shot-type',
        ]),
      )
    : hiddenTabs;

  const libraryTabs = (
    [
      {
        id: 'all',
        label: t('All uploads'),
        icon: <CollectionIcon className='h-5 w-5' />,
      },
      {
        id: 'character',
        label: t('Character'),
        icon: <UserIcon className='h-5 w-5' />,
      },
      {
        id: 'object',
        label: t('Object'),
        icon: <CubeIcon className='h-5 w-5' />,
      },
      {
        id: 'history',
        label: t('History'),
        icon: <PhotographIcon className='h-5 w-5' />,
      },
      // Video tab is opt-in and pick-mode picks a single image, never a video.
      ...(showVideoTab && !pickMode
        ? [
            {
              id: 'video' as const,
              label: t('Video'),
              icon: <VideoCameraIcon className='h-5 w-5' />,
            },
          ]
        : []),
      // Audio tab is opt-in (Video Generator reference audio) and pick-mode
      // picks a single image, never audio.
      ...(showAudioTab && !pickMode
        ? [
            {
              id: 'audio' as const,
              label: t('Audio'),
              icon: <MusicNoteIcon className='h-5 w-5' />,
            },
          ]
        : []),
    ] as { id: SceneTab; label: string; icon: JSX.Element }[]
  ).filter((item) => !effectiveHiddenTabs.includes(item.id));

  const sceneTabs = (
    [
      {
        id: 'style',
        label: t('Style'),
        icon: <StarIcon className='h-5 w-5' />,
      },
      {
        id: 'camera',
        label: t('Camera'),
        icon: <CameraIcon className='h-5 w-5' />,
      },
      {
        id: 'lighting',
        label: t('Lighting'),
        icon: <LightBulbIcon className='h-5 w-5' />,
      },
      {
        id: 'shot-type',
        label: t('Shot Type'),
        icon: <FilmIcon className='h-5 w-5' />,
      },
    ] as { id: SceneTab; label: string; icon: JSX.Element }[]
  ).filter((item) => !effectiveHiddenTabs.includes(item.id));

  const handleApply = () => {
    onApply?.({
      style,
      camera,
      lens: isFixedOptics ? 'Standard' : lens,
      focalLength: isFixedOptics ? 'Standard' : focalLength,
      aperture: isFixedOptics ? 'Standard' : aperture,
      lighting,
      shotType,
    });
    onLibrarySelectionApply?.({
      characters: characterSelection,
      objects: objectSelection,
      images: imageSelection,
      ...(showVideoTab ? { video: videoSelection } : {}),
      ...(showAudioTab ? { audio: audioSelection } : {}),
    });
    onClose();
  };

  // Drop an audio selection the modal created but never handed to the parent
  // (its object URL would otherwise leak). Ownership is tracked by objectUrl,
  // not object identity — trim edits clone the value but share the URL, and
  // the parent's URL must never be revoked here.
  const discardUnappliedAudio = () => {
    setAudioSelection((prev) => {
      if (prev && prev.objectUrl !== selectedAudio?.objectUrl) {
        URL.revokeObjectURL(prev.objectUrl);
      }
      return null;
    });
  };

  const handleCancelClose = () => {
    if (showAudioTab) discardUnappliedAudio();
    onClose();
  };

  const handleClearCurrent = () => {
    if (tab === 'all') {
      setCharacterSelection([]);
      setObjectSelection([]);
      setImageSelection([]);
      if (showVideoTab) setVideoSelection('');
    }
    if (tab === 'character') setCharacterSelection([]);
    if (tab === 'object') setObjectSelection([]);
    if (tab === 'history') {
      setImageSelection(imageSelection.filter((u) => !historyUrlSet.has(u)));
      if (showVideoTab) setVideoSelection('');
    }
    if (tab === 'video') setVideoSelection('');
    if (tab === 'audio') discardUnappliedAudio();
    if (tab === 'style') setStyle('None');
    if (tab === 'camera') {
      setCamera('Standard');
      setLens('Standard');
      setFocalLength('Standard');
      setAperture('Standard');
    }
    if (tab === 'lighting') setLighting('None');
    if (tab === 'shot-type') setShotType('None');
  };

  const filteredStyles = useMemo(
    () =>
      STYLE_OPTIONS.filter(
        (o) =>
          o.value !== 'None' &&
          o.value.toLowerCase().includes(styleSearch.toLowerCase()),
      ),
    [styleSearch],
  );

  const filteredLighting = useMemo(
    () =>
      LIGHTING_OPTIONS.filter(
        (o) =>
          o.label !== 'None' &&
          o.label.toLowerCase().includes(lightingSearch.toLowerCase()),
      ),
    [lightingSearch],
  );

  return (
    <Modal
      showModal={isOpen}
      onClose={handleCancelClose}
      className='sm:max-w-6xl'
    >
      <div className='flex min-h-[640px] flex-col sm:flex-row'>
        <nav className='scrollbar-hide flex shrink-0 flex-row gap-1 overflow-x-auto border-b border-gray-200 p-3 pr-14 dark:border-slate-700 sm:w-56 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:pr-3'>
          {libraryTabs.length > 0 && (
            <h3 className='mb-1 hidden px-2 pt-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 sm:block'>
              {t('Library')}
            </h3>
          )}
          {libraryTabs.map((item) => (
            <SidebarTabButton
              key={item.id}
              item={item}
              active={tab === item.id}
              value={summary[item.id]}
              onClick={() => setTab(item.id)}
            />
          ))}
          {sceneTabs.length > 0 && (
            <h3 className='mb-1 mt-3 hidden px-2 pt-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 sm:block'>
              {t('Scene')}
            </h3>
          )}
          {sceneTabs.map((item) => (
            <SidebarTabButton
              key={item.id}
              item={item}
              active={tab === item.id}
              value={summary[item.id]}
              onClick={() => setTab(item.id)}
            />
          ))}
        </nav>

        <div className='flex min-w-0 flex-1 flex-col'>
          <div className='flex-1 overflow-y-auto p-6'>
            {tab === 'all' && (
              <AllUploadsTab
                characterSelection={characterSelection}
                objectSelection={objectSelection}
                imageSelection={imageSelection}
                onCharacterChange={setCharacterSelection}
                onObjectChange={setObjectSelection}
                onImageChange={setImageSelection}
                onPick={onPick}
                showVideos={showVideoTab && !pickMode}
                selectedVideo={videoSelection}
                onVideoChange={setVideoSelection}
                videoLimits={videoLimits}
                showAudio={showAudioTab && !pickMode}
                audioMaxSeconds={audioMaxSeconds}
                onAudioPicked={(value) => {
                  setAudioSelection((prev) => {
                    if (
                      prev &&
                      prev.objectUrl !== selectedAudio?.objectUrl &&
                      prev.objectUrl !== value.objectUrl
                    ) {
                      URL.revokeObjectURL(prev.objectUrl);
                    }
                    return value;
                  });
                  // Jump to the Audio tab so the trimmer is immediately
                  // visible (the audio has no tile in the uploads grid).
                  setTab('audio');
                }}
              />
            )}

            {tab === 'character' && (
              <LibraryTab
                kind='character'
                title={t('Choose characters')}
                selected={characterSelection}
                onChange={setCharacterSelection}
                onPick={onPick}
                showCustomActors={showCustomActors}
              />
            )}

            {tab === 'object' && (
              <LibraryTab
                kind='object'
                title={t('Choose objects')}
                selected={objectSelection}
                onChange={setObjectSelection}
                onPick={onPick}
              />
            )}

            {tab === 'history' && (
              <HistoryTab
                selected={imageSelection}
                onChange={setImageSelection}
                onLoadedUrlsChange={setHistoryUrls}
                onPick={onPick}
                showVideos={showVideoTab && !pickMode}
                selectedVideo={videoSelection}
                onVideoChange={setVideoSelection}
              />
            )}

            {tab === 'video' && (
              <VideoTab
                selected={videoSelection}
                onChange={setVideoSelection}
                limits={videoLimits}
              />
            )}

            {tab === 'audio' && (
              <AudioTab
                selected={audioSelection}
                previouslyApplied={selectedAudio}
                maxSeconds={audioMaxSeconds}
                onChange={setAudioSelection}
              />
            )}

            {tab === 'style' && (
              <>
                <div className='mb-4 flex items-center gap-4 pr-8'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    {t('Choose a style')}
                  </h3>
                  <input
                    type='text'
                    placeholder={t('Search...')}
                    value={styleSearch}
                    onChange={(e) => setStyleSearch(e.target.value)}
                    className='w-40 rounded-full border-gray-300 px-4 py-1.5 text-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  />
                </div>
                <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-5'>
                  {filteredStyles.map((option) => (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => setStyle(option.value)}
                      className={clsxm(
                        'group flex flex-col overflow-hidden rounded-xl border-2 transition-all duration-150',
                        style === option.value
                          ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                          : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                      )}
                    >
                      {option.previewUrl ? (
                        <div className='relative aspect-square w-full'>
                          <Image
                            src={option.previewUrl}
                            alt={option.value}
                            fill
                            sizes='(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw'
                            className='object-cover'
                          />
                        </div>
                      ) : (
                        <div className='flex aspect-square w-full items-center justify-center bg-gray-100 dark:bg-slate-800'>
                          <span className='text-3xl'>{option.emoji}</span>
                        </div>
                      )}
                      <span
                        className={clsxm(
                          'px-2 py-2 text-center text-xs font-medium',
                          style === option.value
                            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300'
                            : 'text-gray-700 dark:text-slate-300',
                        )}
                      >
                        {t(option.value)}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {tab === 'camera' && (
              <>
                <div className='mb-4 flex items-center gap-4 pr-8'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    {cameraMode === 'presets'
                      ? t('Choose a camera style')
                      : t('Camera settings')}
                  </h3>
                  <button
                    type='button'
                    onClick={() =>
                      setCameraMode((m) =>
                        m === 'presets' ? 'advanced' : 'presets',
                      )
                    }
                    className='text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
                  >
                    {cameraMode === 'presets'
                      ? `${t('Advanced')} →`
                      : `← ${t('Back to presets')}`}
                  </button>
                </div>

                {cameraMode === 'presets' ? (
                  <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4'>
                    {CAMERA_PRESETS.map((preset) => {
                      const isSelected = matchingPreset?.id === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type='button'
                          onClick={() => {
                            setCamera(preset.camera);
                            setLens(preset.lens);
                            setFocalLength(preset.focalLength);
                            setAperture(preset.aperture);
                          }}
                          className={clsxm(
                            'group flex flex-col overflow-hidden rounded-xl border-2 text-left transition-all duration-150',
                            isSelected
                              ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                              : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                          )}
                        >
                          {preset.previewUrl ? (
                            <div className='relative aspect-square w-full'>
                              <Image
                                src={preset.previewUrl}
                                alt={preset.label}
                                fill
                                sizes='(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw'
                                className='object-cover'
                              />
                            </div>
                          ) : (
                            <div className='flex aspect-square w-full items-center justify-center bg-gray-100 dark:bg-slate-800'>
                              <span className='text-xs text-gray-400'>
                                {t('No preview')}
                              </span>
                            </div>
                          )}
                          <div
                            className={clsxm(
                              'px-3 py-2',
                              isSelected
                                ? 'bg-indigo-50 dark:bg-indigo-950/30'
                                : 'bg-white dark:bg-slate-800',
                            )}
                          >
                            <p
                              className={clsxm(
                                'text-sm font-semibold',
                                isSelected
                                  ? 'text-indigo-700 dark:text-indigo-300'
                                  : 'text-gray-900 dark:text-slate-200',
                              )}
                            >
                              {preset.label}
                            </p>
                            <p className='line-clamp-1 text-xs text-gray-500 dark:text-slate-400'>
                              {preset.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className={clsxm(
                      'grid gap-3',
                      isFixedOptics
                        ? 'grid-cols-1'
                        : 'grid-cols-2 sm:grid-cols-4',
                    )}
                  >
                    <AdvancedColumn
                      title={t('Camera')}
                      options={CAMERA_OPTIONS}
                      selected={camera}
                      onSelect={(label) => {
                        setCamera(label);
                        if (FIXED_OPTICS_CAMERAS.has(label)) {
                          setLens('Standard');
                          setFocalLength('Standard');
                          setAperture('Standard');
                        }
                      }}
                    />
                    {!isFixedOptics && (
                      <>
                        <AdvancedColumn
                          title={t('Lens')}
                          options={LENS_OPTIONS}
                          selected={lens}
                          onSelect={setLens}
                        />
                        <AdvancedColumn
                          title={t('Focal Length')}
                          options={FOCAL_LENGTH_OPTIONS}
                          selected={focalLength}
                          onSelect={setFocalLength}
                        />
                        <AdvancedColumn
                          title={t('Aperture')}
                          options={APERTURE_OPTIONS}
                          selected={aperture}
                          onSelect={setAperture}
                        />
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {tab === 'lighting' && (
              <>
                <div className='mb-4 flex items-center gap-4 pr-8'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    {t('Select Lighting')}
                  </h3>
                  <input
                    type='text'
                    placeholder={t('Search...')}
                    value={lightingSearch}
                    onChange={(e) => setLightingSearch(e.target.value)}
                    className='w-40 rounded-full border-gray-300 px-4 py-1.5 text-sm focus:border-primary-500 focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                  />
                </div>
                <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-5'>
                  {filteredLighting.map((option) => (
                    <button
                      key={option.label}
                      type='button'
                      onClick={() => setLighting(option.label)}
                      className={clsxm(
                        'group flex flex-col overflow-hidden rounded-xl border-2 transition-all duration-150',
                        lighting === option.label
                          ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                          : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                      )}
                    >
                      {option.previewUrl ? (
                        <div className='relative aspect-square w-full'>
                          <Image
                            src={option.previewUrl}
                            alt={option.label}
                            fill
                            sizes='(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw'
                            className='object-cover'
                          />
                        </div>
                      ) : (
                        <div className='flex aspect-square w-full items-center justify-center bg-gray-100 dark:bg-slate-800'>
                          <span className='text-xs text-gray-400'>
                            {t('No preview')}
                          </span>
                        </div>
                      )}
                      <span
                        className={clsxm(
                          'px-2 py-2 text-center text-xs font-medium',
                          lighting === option.label
                            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300'
                            : 'text-gray-700 dark:text-slate-300',
                        )}
                      >
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {tab === 'shot-type' && (
              <>
                <h3 className='mb-4 text-lg font-semibold text-gray-900 dark:text-white'>
                  {t('Select Shot Type')}
                </h3>
                <div className='max-h-[60vh] space-y-5 overflow-y-auto'>
                  {SHOT_GROUPS.map((group) => {
                    const options = SHOT_TYPE_OPTIONS.filter(
                      (o) =>
                        o.label !== 'None' && o.label.startsWith(group.prefix),
                    );
                    if (options.length === 0) return null;

                    return (
                      <div key={group.name}>
                        <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400'>
                          {group.name}
                        </p>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
                          {options.map((option) => {
                            const angle = option.label
                              .replace(group.prefix + ' - ', '')
                              .trim();
                            return (
                              <button
                                key={option.label}
                                type='button'
                                onClick={() => setShotType(option.label)}
                                className={clsxm(
                                  'flex flex-col overflow-hidden rounded-xl border-2 transition-all duration-150',
                                  shotType === option.label
                                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                                )}
                              >
                                {option.previewUrl ? (
                                  <div className='relative aspect-[4/3] w-full'>
                                    <Image
                                      src={option.previewUrl}
                                      alt={option.label}
                                      fill
                                      sizes='(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw'
                                      className='object-cover'
                                    />
                                  </div>
                                ) : (
                                  <div className='flex aspect-[4/3] w-full items-center justify-center bg-gray-100 dark:bg-slate-800'>
                                    <span className='text-xs text-gray-400'>
                                      {t('No preview')}
                                    </span>
                                  </div>
                                )}
                                <span
                                  className={clsxm(
                                    'px-2 py-2 text-center text-xs font-medium',
                                    shotType === option.label
                                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300'
                                      : 'text-gray-700 dark:text-slate-300',
                                  )}
                                >
                                  {angle}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {pickMode ? (
            <div className='flex items-center justify-end border-t border-gray-200 p-4 dark:border-slate-700'>
              <Button variant='light' onClick={onClose}>
                {t('Cancel')}
              </Button>
            </div>
          ) : (
            <div className='flex items-center justify-between border-t border-gray-200 p-4 dark:border-slate-700'>
              <button
                type='button'
                onClick={handleClearCurrent}
                className='text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-300'
              >
                {t('Clear selection')}
              </button>
              <div className='flex gap-3'>
                <Button variant='light' onClick={handleCancelClose}>
                  {t('Cancel')}
                </Button>
                <Button variant='primary' onClick={handleApply}>
                  {t('Apply')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function AdvancedColumn({
  title,
  options,
  selected,
  onSelect,
}: {
  title: string;
  options: CinematicOption[];
  selected: string;
  onSelect: (label: string) => void;
}) {
  return (
    <div className='flex flex-col rounded-xl border border-gray-200 dark:border-slate-700'>
      <p className='border-b border-gray-200 px-3 py-2.5 text-center text-sm font-semibold text-gray-900 dark:border-slate-700 dark:text-white'>
        {title}
      </p>
      <div className='max-h-[50vh] space-y-0.5 overflow-y-auto p-1'>
        {options.map((option) => (
          <button
            key={option.label}
            type='button'
            onClick={() => onSelect(option.label)}
            className={clsxm(
              'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors',
              selected === option.label
                ? 'bg-gray-100 font-semibold text-gray-900 dark:bg-slate-700 dark:text-white'
                : 'text-gray-700 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800',
            )}
          >
            <span className='truncate text-sm'>{option.label}</span>
            {option.previewUrl ? (
              <div className='relative h-14 w-14 flex-shrink-0 overflow-hidden rounded'>
                <Image
                  src={option.previewUrl}
                  alt={option.label}
                  fill
                  sizes='56px'
                  className='object-cover'
                />
              </div>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function SidebarTabButton({
  item,
  active,
  value,
  onClick,
}: {
  item: { id: SceneTab; label: string; icon: JSX.Element };
  active: boolean;
  value: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={clsxm(
        'flex shrink-0 items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
        active
          ? 'bg-gray-100 text-gray-900 dark:bg-slate-700 dark:text-white'
          : 'text-gray-600 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-800',
      )}
    >
      <span
        className={clsxm(
          'flex-shrink-0',
          active
            ? 'text-primary-600 dark:text-primary-300'
            : 'text-gray-400 dark:text-slate-500',
        )}
      >
        {item.icon}
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-medium'>{item.label}</span>
        {value && (
          <span className='block truncate text-xs text-gray-500 dark:text-slate-400'>
            {value}
          </span>
        )}
      </span>
    </button>
  );
}

const KIND_LABEL: Record<UserUploadKind, string> = {
  character: 'character',
  object: 'object',
  image: 'image',
  video: 'video',
};

// Always-visible three-dot menu on a tile — hover-only actions are unusable
// on touch devices.
function TileMenu({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete: () => void;
}) {
  const { t } = useLocale('ai-images');
  return (
    <Menu as='div' className='absolute left-1.5 top-1.5 z-10'>
      <Menu.Button
        className='inline-flex items-center rounded-full bg-black/60 p-1.5 text-white shadow-sm transition hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black/60'
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <span className='sr-only'>{t('Open options')}</span>
        <DotsVerticalIcon className='h-4 w-4' aria-hidden='true' />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'
      >
        <Menu.Items className='absolute left-0 z-20 mt-1 w-36 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-slate-800 dark:ring-slate-600'>
          {onEdit && (
            <Menu.Item>
              {({ active }) => (
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className={clsxm(
                    'flex w-full items-center px-3 py-2 text-sm text-gray-700 dark:text-slate-200',
                    active && 'bg-gray-100 dark:bg-slate-700',
                  )}
                >
                  <PencilIcon className='mr-2.5 h-4 w-4 text-gray-400' />
                  {t('Edit')}
                </button>
              )}
            </Menu.Item>
          )}
          <Menu.Item>
            {({ active }) => (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className={clsxm(
                  'flex w-full items-center px-3 py-2 text-sm text-gray-700 dark:text-slate-200',
                  active && 'bg-gray-100 dark:bg-slate-700',
                )}
              >
                <TrashIcon className='mr-2.5 h-4 w-4 text-red-500' />
                {t('Delete')}
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function AllUploadsTab({
  characterSelection,
  objectSelection,
  imageSelection,
  onCharacterChange,
  onObjectChange,
  onImageChange,
  onPick,
  showVideos = false,
  selectedVideo = '',
  videoLimits = DEFAULT_REFERENCE_VIDEO_LIMITS,
  onVideoChange,
  showAudio = false,
  audioMaxSeconds = 15,
  onAudioPicked,
}: {
  characterSelection: string[];
  objectSelection: string[];
  imageSelection: string[];
  onCharacterChange: (next: string[]) => void;
  onObjectChange: (next: string[]) => void;
  onImageChange: (next: string[]) => void;
  onPick?: (url: string) => void;
  /** Also list uploaded videos, selectable as the reference video to edit. */
  showVideos?: boolean;
  selectedVideo?: string;
  videoLimits?: ReferenceVideoLimits;
  onVideoChange?: (url: string) => void;
  /** Also accept audio files in Upload new (routed to the Audio tab). */
  showAudio?: boolean;
  audioMaxSeconds?: number;
  onAudioPicked?: (value: ReferenceAudioValue) => void;
}) {
  const { t } = useLocale('ai-images');
  const { user } = useUser();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: allUploads = [], isLoading } = useQuery({
    queryKey: ['user-uploads', 'all'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ uploads: UserUpload[] }>(
        `/api/user-uploads`,
      );
      return res.data.uploads ?? [];
    },
    staleTime: 30_000,
  });
  // Videos only appear when the consumer supports a reference video
  // (e.g. /ai-videos); the AI Images picker stays images-only.
  const uploads = useMemo(
    () =>
      showVideos ? allUploads : allUploads.filter((u) => u.kind !== 'video'),
    [allUploads, showVideos],
  );

  // On the Video Generator the tile also takes videos (→ reference video) and
  // audio (→ Audio tab trimmer); the AI Images picker stays images-only.
  const acceptTypes = [
    'image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp',
    ...(showVideos ? ['video/mp4,video/quicktime,.mp4,.mov'] : []),
    ...(showAudio ? [ACCEPTED_AUDIO] : []),
  ].join(',');

  const isAudioFile = (file: File) =>
    file.type.startsWith('audio/') ||
    /\.(mp3|wav|m4a|aac|ogg|oga|flac)$/i.test(file.name);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    const audioFiles = showAudio ? list.filter(isAudioFile) : [];
    const videoFiles = showVideos
      ? list.filter((f) => !isAudioFile(f) && f.type.startsWith('video/'))
      : [];
    const imageFiles = list.filter(
      (f) => !isAudioFile(f) && !f.type.startsWith('video/'),
    );
    // A generation takes ONE reference video and ONE audio clip.
    if (audioFiles.length > 1 || videoFiles.length > 1) {
      toast.error(t('Only one video and one audio file can be attached.'));
    }

    setIsUploading(true);
    try {
      if (imageFiles.length > 0) {
        try {
          const uploaded = await Promise.all(
            imageFiles.map(async (file) =>
              uploadUserImage(
                // Downscale/re-encode oversized photos before saving so large
                // camera images stay within model upload limits (long edge 3840).
                await compressImage(file, { force: true, format: 'jpeg' }),
                'image',
              ),
            ),
          );
          const newUrls = uploaded.map((u) => u.image_url);
          if (newUrls.length > 0) {
            queryClient.invalidateQueries({
              queryKey: ['user-uploads', 'image'],
            });
            queryClient.invalidateQueries({
              queryKey: ['user-uploads', 'all'],
            });
            if (onPick) {
              onPick(newUrls[0]);
            } else {
              onImageChange([...imageSelection, ...newUrls]);
            }
          }
        } catch (error) {
          console.error('Upload failed', error);
          toast.error(t('Failed to upload image. Please try again.'));
        }
      }

      if (videoFiles.length > 0 && user) {
        const publicUrl = await uploadReferenceVideoFile(
          videoFiles[0],
          user.id,
          t,
          videoLimits,
        );
        if (publicUrl) {
          onVideoChange?.(publicUrl);
          void queryClient.invalidateQueries({
            queryKey: ['user-uploads', 'video'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['user-uploads', 'all'],
          });
        }
      }

      if (audioFiles.length > 0) {
        const audioFile = audioFiles[0];
        const validationError = validateReferenceAudioFile(audioFile);
        if (validationError) {
          toast.error(t(validationError));
        } else {
          try {
            const decoded = await decodeReferenceAudioFile(
              audioFile,
              audioMaxSeconds,
            );
            if (decoded.durationSec < 0.5) {
              URL.revokeObjectURL(decoded.objectUrl);
              toast.error(t('This audio file is too short.'));
            } else {
              onAudioPicked?.(decoded);
            }
          } catch {
            toast.error(t('Could not read this audio file. Try MP3 or WAV.'));
          }
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  const setterFor = (kind: UserUploadKind) =>
    kind === 'character'
      ? onCharacterChange
      : kind === 'object'
        ? onObjectChange
        : onImageChange;

  const selectionFor = (kind: UserUploadKind) =>
    kind === 'character'
      ? characterSelection
      : kind === 'object'
        ? objectSelection
        : imageSelection;

  const toggle = (upload: UserUpload) => {
    const kind = upload.kind;
    const current = selectionFor(kind);
    const setter = setterFor(kind);
    setter(
      current.includes(upload.image_url)
        ? current.filter((u) => u !== upload.image_url)
        : [...current, upload.image_url],
    );
  };

  const isSelected = (upload: UserUpload) =>
    selectionFor(upload.kind).includes(upload.image_url);

  const handleDelete = async (upload: UserUpload) => {
    if (!window.confirm(t('Delete this image? This cannot be undone.'))) return;
    try {
      await axiosInstance.delete('/api/user-uploads', {
        data: { id: upload.id },
      });
      if (upload.kind === 'video') {
        if (selectedVideo === upload.image_url) onVideoChange?.('');
      } else {
        const setter = setterFor(upload.kind);
        setter(selectionFor(upload.kind).filter((u) => u !== upload.image_url));
      }
      queryClient.setQueryData<UserUpload[]>(
        ['user-uploads', 'all'],
        (prev = []) => prev.filter((u) => u.id !== upload.id),
      );
      queryClient.invalidateQueries({
        queryKey: ['user-uploads', upload.kind],
      });
    } catch (error) {
      console.error('Delete failed', error);
      toast.error(t('Failed to delete image.'));
    }
  };

  return (
    <>
      <div className='mb-4 flex items-center justify-between gap-4 pr-8'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {t('All uploads')}
        </h3>
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept={acceptTypes}
        multiple
        className='sr-only'
        disabled={isUploading}
        onChange={(e) => {
          handleUpload(e.target.files);
          e.target.value = '';
        }}
      />

      <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-5'>
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className='group flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-white text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:border-slate-500'
        >
          {isUploading ? (
            <Spinner className='h-7 w-7 text-primary-500' />
          ) : (
            <>
              <PlusIcon className='h-7 w-7' />
              <span className='text-xs font-medium'>{t('Upload new')}</span>
              {(showVideos || showAudio) && (
                <span className='px-2 text-center text-[10px] text-gray-400 dark:text-slate-500'>
                  {showVideos && showAudio
                    ? t('Images, video & audio')
                    : showVideos
                      ? t('Images & video')
                      : t('Images & audio')}
                </span>
              )}
            </>
          )}
        </button>

        {isLoading && uploads.length === 0 && (
          <div className='col-span-full flex justify-center py-8'>
            <Spinner />
          </div>
        )}
        {!isLoading && uploads.length === 0 && (
          <div className='col-span-2 py-12 text-center text-sm text-gray-500 dark:text-slate-400 lg:col-span-4'>
            {t('No uploads yet. Upload a new image to get started.')}
          </div>
        )}
        {uploads.map((upload) => {
          const isVideo = upload.kind === 'video';
          const selected = isVideo
            ? selectedVideo === upload.image_url
            : isSelected(upload);
          return (
            <div key={upload.id} className='group relative'>
              <button
                type='button'
                onClick={() => {
                  if (isVideo) {
                    onVideoChange?.(selected ? '' : upload.image_url);
                  } else if (onPick) {
                    onPick(upload.image_url);
                  } else {
                    toggle(upload);
                  }
                }}
                className={clsxm(
                  'relative flex aspect-square w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                  selected
                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                )}
              >
                {isVideo ? (
                  <video
                    src={upload.image_url}
                    className='h-full w-full object-cover'
                    muted
                    loop
                    playsInline
                    preload='metadata'
                    onMouseEnter={(e) =>
                      void e.currentTarget.play().catch(() => {})
                    }
                    onMouseLeave={(e) => e.currentTarget.pause()}
                  />
                ) : (
                  <Image
                    src={upload.image_url}
                    alt={upload.name || 'upload'}
                    fill
                    sizes='(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw'
                    className='object-cover'
                  />
                )}
                <span className='absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white'>
                  {t(KIND_LABEL[upload.kind])}
                </span>
                {selected && (
                  <span className='absolute right-2 top-2 rounded-full bg-white text-indigo-600 shadow dark:bg-slate-900'>
                    <CheckCircleIcon className='h-6 w-6' />
                  </span>
                )}
              </button>
              <TileMenu onDelete={() => handleDelete(upload)} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function LibraryTab({
  kind,
  title,
  selected,
  onChange,
  onPick,
  showCustomActors,
}: {
  kind: UserUploadKind;
  title: string;
  selected: string[];
  onChange: (next: string[]) => void;
  onPick?: (url: string) => void;
  showCustomActors?: boolean;
}) {
  const { t } = useLocale('ai-images');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  // User-created (custom) actors, shown as pickable characters.
  const { data: actors = [] } = useQuery({
    queryKey: ['custom-actors'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ actors: Actor[] }>(
        `/api/custom-actors`,
      );
      return res.data.actors ?? [];
    },
    enabled: !!showCustomActors,
    staleTime: 60_000,
  });

  const { data: uploads = [], isLoading } = useQuery({
    queryKey: ['user-uploads', kind],
    queryFn: async () => {
      const res = await axiosInstance.get<{ uploads: UserUpload[] }>(
        `/api/user-uploads?kind=${kind}`,
      );
      return res.data.uploads ?? [];
    },
    staleTime: 30_000,
  });

  const toggle = (url: string) => {
    onChange(
      selected.includes(url)
        ? selected.filter((u) => u !== url)
        : [...selected, url],
    );
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map(async (file) =>
          uploadUserImage(
            // Downscale/re-encode oversized photos before saving so large
            // camera images stay within model upload limits (long edge 3840).
            await compressImage(file, { force: true, format: 'jpeg' }),
            kind,
          ),
        ),
      );
      const newUrls = uploaded.map((u) => u.image_url);
      if (newUrls.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['user-uploads', kind] });
        queryClient.invalidateQueries({ queryKey: ['user-uploads', 'all'] });
        if (onPick) {
          onPick(newUrls[0]);
        } else {
          onChange([...selected, ...newUrls]);
        }
      }
    } catch (error) {
      console.error('Upload failed', error);
      toast.error(t('Failed to upload image. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (upload: UserUpload) => {
    if (!window.confirm(t('Delete this image? This cannot be undone.'))) return;
    try {
      await axiosInstance.delete('/api/user-uploads', {
        data: { id: upload.id },
      });
      onChange(selected.filter((u) => u !== upload.image_url));
      queryClient.setQueryData<UserUpload[]>(
        ['user-uploads', kind],
        (prev = []) => prev.filter((u) => u.id !== upload.id),
      );
      queryClient.invalidateQueries({ queryKey: ['user-uploads', 'all'] });
    } catch (error) {
      console.error('Delete failed', error);
      toast.error(t('Failed to delete image.'));
    }
  };

  // Remove a user-created (custom) actor.
  const handleDeleteActor = async (actor: Actor) => {
    if (!window.confirm(t('Remove this actor? This cannot be undone.'))) return;
    try {
      await axiosInstance.delete('/api/custom-actors', {
        data: { id: actor.id },
      });
      onChange(selected.filter((u) => u !== actor.photo));
      queryClient.setQueryData<Actor[]>(['custom-actors'], (prev = []) =>
        prev.filter((a) => a.id !== actor.id),
      );
    } catch (error) {
      console.error('Delete actor failed', error);
      toast.error(t('Failed to remove actor.'));
    }
  };

  // Image being edited in the EditModal (an upload or actor photo).
  const [editingUrl, setEditingUrl] = useState<string | null>(null);
  // Edited image URLs already saved, to avoid inserting the same one twice.
  const insertedEditsRef = useRef<Set<string>>(new Set());

  // After a successful edit, save the result as a new library item (same kind).
  const handleEditInsert = async (url?: string) => {
    if (!url || insertedEditsRef.current.has(url)) return;
    insertedEditsRef.current.add(url);
    try {
      await axiosInstance.post('/api/user-uploads', {
        kind,
        image_url: url,
        name: 'Edited',
      });
      queryClient.invalidateQueries({ queryKey: ['user-uploads', kind] });
      queryClient.invalidateQueries({ queryKey: ['user-uploads', 'all'] });
    } catch (error) {
      console.error('Saving edited image failed', error);
      toast.error(t('Failed to save edited image.'));
    }
  };

  return (
    <>
      <div className='mb-4 flex items-center justify-between gap-4 pr-8'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {title}
        </h3>
        {selected.length > 0 && (
          <span className='text-xs text-gray-500 dark:text-slate-400'>
            {selected.length} {t('selected')}
          </span>
        )}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept='image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp'
        multiple
        className='sr-only'
        disabled={isUploading}
        onChange={(e) => {
          handleUpload(e.target.files);
          e.target.value = '';
        }}
      />

      <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 lg:grid-cols-5'>
        <button
          type='button'
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className='group flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-white text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-400 dark:hover:border-slate-500'
        >
          {isUploading ? (
            <Spinner className='h-7 w-7 text-primary-500' />
          ) : (
            <>
              <PlusIcon className='h-7 w-7' />
              <span className='text-xs font-medium'>{t('Upload new')}</span>
            </>
          )}
        </button>

        {isLoading && uploads.length === 0 && (
          <div className='col-span-full flex justify-center py-8'>
            <Spinner />
          </div>
        )}

        {uploads.map((upload) => {
          const isSelected = selected.includes(upload.image_url);
          return (
            <div key={upload.id} className='group relative'>
              <button
                type='button'
                onClick={() =>
                  onPick ? onPick(upload.image_url) : toggle(upload.image_url)
                }
                className={clsxm(
                  'relative flex aspect-square w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                  isSelected
                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                )}
              >
                <Image
                  src={upload.image_url}
                  alt={upload.name || 'upload'}
                  fill
                  sizes='(min-width: 1024px) 20vw, (min-width: 640px) 33vw, 50vw'
                  className='object-cover'
                />
                {isSelected && (
                  <span className='absolute right-2 top-2 rounded-full bg-white text-indigo-600 shadow dark:bg-slate-900'>
                    <CheckCircleIcon className='h-6 w-6' />
                  </span>
                )}
              </button>
              <TileMenu
                onEdit={() => setEditingUrl(upload.image_url)}
                onDelete={() => handleDelete(upload)}
              />
            </div>
          );
        })}

        {actors.map((actor) => {
          const isActorSelected = selected.includes(actor.photo);
          return (
            <div key={actor.id} className='group relative'>
              <button
                type='button'
                onClick={() =>
                  onPick ? onPick(actor.photo) : toggle(actor.photo)
                }
                title={actor.name}
                className={clsxm(
                  'relative flex aspect-square w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                  isActorSelected
                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                )}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={actor.photo}
                  alt={actor.name}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
                {isActorSelected && (
                  <span className='absolute right-2 top-2 rounded-full bg-white text-indigo-600 shadow dark:bg-slate-900'>
                    <CheckCircleIcon className='h-6 w-6' />
                  </span>
                )}
              </button>
              <TileMenu
                onEdit={() => setEditingUrl(actor.photo)}
                onDelete={() => handleDeleteActor(actor)}
              />
            </div>
          );
        })}
      </div>

      {editingUrl && (
        <EditModal
          image={{ image_url: editingUrl }}
          isOpen={true}
          onClose={() => setEditingUrl(null)}
          onUpdate={(updated) => handleEditInsert(updated.image_url)}
        />
      )}
    </>
  );
}

interface GeneratedImage {
  id: number;
  image_url: string;
  prompt?: string;
  is_video?: boolean;
  created_at?: string;
}

const HISTORY_PAGE_SIZE = 30;

function HistoryTab({
  selected,
  onChange,
  onLoadedUrlsChange,
  onPick,
  showVideos = false,
  selectedVideo = '',
  onVideoChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  onLoadedUrlsChange: (urls: string[]) => void;
  onPick?: (url: string) => void;
  /** Also list generated videos, selectable as the reference video to edit. */
  showVideos?: boolean;
  selectedVideo?: string;
  onVideoChange?: (url: string) => void;
}) {
  const { t } = useLocale('ai-images');

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['scene-history-images', showVideos],
    async ({ pageParam = 1 }) => {
      // Without images_only the endpoint returns generated images AND videos.
      const res = await axiosInstance.get<{
        data: GeneratedImage[];
        count: number;
      }>(
        `/api/images/paginated?page=${pageParam}&limit=${HISTORY_PAGE_SIZE}${
          showVideos ? '' : '&images_only=true'
        }`,
      );
      return res.data;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce(
          (acc, p) => acc + (p.data?.length ?? 0),
          0,
        );
        if (loaded >= (lastPage.count ?? 0)) return undefined;
        return allPages.length + 1;
      },
      staleTime: 30_000,
    },
  );

  const items = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((p) => p.data ?? [])
        .filter((img) => img.image_url && (showVideos || !img.is_video)),
    [data, showVideos],
  );
  const images = useMemo(() => items.filter((i) => !i.is_video), [items]);

  // Report loaded generated-image URLs up so the parent can scope the History
  // tab's clear/count to generated picks (uploads share the same bucket).
  useEffect(() => {
    onLoadedUrlsChange(images.map((img) => img.image_url));
  }, [images, onLoadedUrlsChange]);

  const toggle = (url: string) => {
    onChange(
      selected.includes(url)
        ? selected.filter((u) => u !== url)
        : [...selected, url],
    );
  };

  const selectedCount = images.filter((img) =>
    selected.includes(img.image_url),
  ).length;

  return (
    <>
      <div className='mb-4 flex items-center justify-between gap-4 pr-8'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {showVideos ? t('Generated media') : t('Generated images')}
        </h3>
        {selectedCount > 0 && (
          <span className='text-xs text-gray-500 dark:text-slate-400'>
            {selectedCount} {t('selected')}
          </span>
        )}
      </div>

      <div className='max-h-[60vh] overflow-y-auto'>
        <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
          {isLoading && items.length === 0 && (
            <div className='col-span-full flex justify-center py-8'>
              <Spinner />
            </div>
          )}
          {isError && items.length === 0 && (
            <div className='col-span-full py-12 text-center text-sm text-gray-500 dark:text-slate-400'>
              {t('Failed to load images. Please try again.')}
            </div>
          )}
          {!isLoading && !isError && !hasNextPage && items.length === 0 && (
            <div className='col-span-full py-12 text-center text-sm text-gray-500 dark:text-slate-400'>
              {t('No generated images yet. Create some images first!')}
            </div>
          )}
          {items.map((img) => {
            if (img.is_video) {
              const isSelected = selectedVideo === img.image_url;
              return (
                <button
                  key={img.id}
                  type='button'
                  onClick={() =>
                    onVideoChange?.(isSelected ? '' : img.image_url)
                  }
                  title={img.prompt || undefined}
                  className={clsxm(
                    'group relative flex aspect-square w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                    isSelected
                      ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                      : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                  )}
                >
                  <video
                    src={img.image_url}
                    className='h-full w-full object-cover'
                    muted
                    loop
                    playsInline
                    preload='metadata'
                    onMouseEnter={(e) =>
                      void e.currentTarget.play().catch(() => {})
                    }
                    onMouseLeave={(e) => e.currentTarget.pause()}
                  />
                  <span className='absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white'>
                    {t('video')}
                  </span>
                  {isSelected && (
                    <span className='absolute right-2 top-2 rounded-full bg-white text-indigo-600 shadow dark:bg-slate-900'>
                      <CheckCircleIcon className='h-6 w-6' />
                    </span>
                  )}
                </button>
              );
            }
            const isSelected = selected.includes(img.image_url);
            return (
              <button
                key={img.id}
                type='button'
                onClick={() =>
                  onPick ? onPick(img.image_url) : toggle(img.image_url)
                }
                title={img.prompt || undefined}
                className={clsxm(
                  'group relative flex aspect-square w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                  isSelected
                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                )}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={img.image_url}
                  alt={img.prompt || 'generated image'}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
                {isSelected && (
                  <span className='absolute right-2 top-2 rounded-full bg-white text-indigo-600 shadow dark:bg-slate-900'>
                    <CheckCircleIcon className='h-6 w-6' />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {hasNextPage && (
          <div className='mt-4 flex justify-center'>
            <button
              type='button'
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className='flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800'
            >
              {isFetchingNextPage ? (
                <>
                  <Spinner className='h-4 w-4' /> {t('Loading...')}
                </>
              ) : (
                t('Load more')
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// Video tab — upload a reference/source video for models that accept one
// (Gemini Omni Flash video editing). Sent inline to the API, hence the tight
// size cap; uploaded to the public `uploads` bucket like motion-control videos.
// Duration is capped because the model's OUTPUT duration mirrors the input and
// is billed per second (see OMNI_VIDEO_EDIT_CREDITS_PER_SECOND). The 0.5s
// tolerance lets users re-edit the model's own ~10.005s outputs. Server-side
// enforcement lives in /api/generate-video (getMp4DurationSeconds).
// Container rounding slack — also what lets users re-edit the model's own
// ~10.005s outputs.
const REF_VIDEO_DURATION_TOLERANCE = 0.5;

/**
 * Validate and upload a reference video file (shared by the Video tab and the
 * All-uploads tab's mixed-media upload). Returns the public URL, or null on
 * failure — validation and upload errors are toasted here.
 */
async function uploadReferenceVideoFile(
  file: File,
  userId: string,
  t: (key: string) => string,
  limits: ReferenceVideoLimits,
): Promise<string | null> {
  const maxSizeMb = Math.round(limits.maxBytes / (1024 * 1024));
  // MP4/MOV only — the server enforces the duration cap by parsing the
  // ISO-BMFF movie header, which WebM doesn't have.
  if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
    toast.error(t('Please upload an MP4 or MOV video file'));
    return null;
  }
  if (file.size > limits.maxBytes) {
    toast.error(t(`Video file must be less than ${maxSizeMb}MB`));
    return null;
  }

  const duration = await new Promise<number | null>((resolve) => {
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.src = URL.createObjectURL(file);
    probe.onloadedmetadata = () => {
      const d = probe.duration;
      URL.revokeObjectURL(probe.src);
      resolve(d);
    };
    probe.onerror = () => {
      URL.revokeObjectURL(probe.src);
      resolve(null);
    };
  });
  if (duration === null) {
    toast.error(t('Error loading video file'));
    return null;
  }
  if (duration > limits.maxSeconds + REF_VIDEO_DURATION_TOLERANCE) {
    toast.error(t(`Video must be no longer than ${limits.maxSeconds} seconds`));
    return null;
  }
  if (duration < limits.minSeconds - REF_VIDEO_DURATION_TOLERANCE) {
    toast.error(t(`Video must be at least ${limits.minSeconds} seconds long`));
    return null;
  }

  try {
    const supabaseClient = createComponentClient();
    const fileExt = file.name.split('.').pop() || 'mp4';
    // Per-user prefix so /api/reference-videos can list them back.
    const filePath = `reference-videos/${userId}/${uuidv4()}.${fileExt}`;
    const { error: uploadError } = await supabaseClient.storage
      .from('uploads')
      .upload(filePath, file);
    if (uploadError) {
      console.error('Reference video upload error:', uploadError);
      toast.error(t('Error uploading video'));
      return null;
    }
    const { data: fileData } = supabaseClient.storage
      .from('uploads')
      .getPublicUrl(filePath);
    if (!fileData?.publicUrl) {
      toast.error(t('Error getting video URL'));
      return null;
    }
    // Record it in user_uploads so it appears in the library next time.
    // Non-fatal: the video is already uploaded and usable either way.
    try {
      await axiosInstance.post(
        '/api/user-uploads',
        {
          kind: 'video',
          image_url: fileData.publicUrl,
          storage_path: filePath,
          name: file.name,
        },
        { skipErrorToast: true },
      );
    } catch (recordError) {
      console.error('Failed to record video upload:', recordError);
    }
    return fileData.publicUrl;
  } catch (error) {
    console.error('Reference video upload error:', error);
    toast.error(t('Error uploading video'));
    return null;
  }
}

function VideoTab({
  selected,
  onChange,
  limits,
}: {
  selected: string;
  onChange: (url: string) => void;
  limits: ReferenceVideoLimits;
}) {
  const { t } = useLocale('ai-images');
  const { user } = useUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const maxSizeMb = Math.round(limits.maxBytes / (1024 * 1024));

  // Previously uploaded reference videos. Tracked in user_uploads
  // (kind 'video') — NEVER list the storage bucket instead: `uploads` is a
  // shared production bucket and storage.list() on it takes ~23s per call.
  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['user-uploads', 'video'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ uploads: UserUpload[] }>(
        '/api/user-uploads?kind=video',
      );
      return res.data.uploads ?? [];
    },
    staleTime: 30_000,
  });

  const handleDelete = async (video: UserUpload) => {
    try {
      await axiosInstance.delete('/api/user-uploads', {
        data: { id: video.id },
      });
      if (selected === video.image_url) onChange('');
      queryClient.setQueryData<UserUpload[]>(
        ['user-uploads', 'video'],
        (prev = []) => prev.filter((v) => v.id !== video.id),
      );
    } catch (error) {
      console.error('Error deleting reference video:', error);
      toast.error(t('Failed to delete video'));
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-triggers onChange.
    event.target.value = '';
    if (!file || !user) return;

    setIsUploading(true);
    try {
      const publicUrl = await uploadReferenceVideoFile(
        file,
        user.id,
        t,
        limits,
      );
      if (publicUrl) {
        onChange(publicUrl);
        void queryClient.invalidateQueries({
          queryKey: ['user-uploads', 'video'],
        });
        void queryClient.invalidateQueries({
          queryKey: ['user-uploads', 'all'],
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <div className='mb-4 pr-8'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {limits.isEditSource ? t('Video to edit') : t('Reference video')}
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-slate-400'>
          {limits.isEditSource
            ? t(
                `Add a video and describe the changes you want in the prompt (up to ${limits.maxSeconds} seconds, ${maxSizeMb}MB). The edited result is up to 10 seconds long.`,
              )
            : t(
                `Add a clip to guide the motion, camera work and pacing of your video (${limits.minSeconds}-${limits.maxSeconds} seconds, up to ${maxSizeMb}MB). Its length is billed on top of the video you generate.`,
              )}
        </p>
      </div>

      <div className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3'>
        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className='flex aspect-video flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-all hover:border-primary-400 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-primary-500 dark:hover:bg-slate-800'
        >
          {isUploading ? (
            <Spinner className='h-6 w-6 text-primary-500' />
          ) : (
            <VideoCameraIcon className='h-6 w-6 text-gray-400 dark:text-slate-500' />
          )}
          <span className='text-sm font-medium text-gray-600 dark:text-slate-300'>
            {isUploading ? t('Uploading...') : t('Upload new')}
          </span>
          <span className='text-xs text-gray-400 dark:text-slate-500'>
            {t('MP4 or MOV')}
          </span>
        </button>

        {videos.map((video) => {
          const isSelected = selected === video.image_url;
          return (
            <div key={video.id} className='group relative'>
              <button
                type='button'
                onClick={() => onChange(isSelected ? '' : video.image_url)}
                className={clsxm(
                  'relative aspect-video w-full overflow-hidden rounded-xl border-2 transition-all duration-150',
                  isSelected
                    ? 'border-indigo-600 shadow-md dark:border-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600',
                )}
              >
                <video
                  src={video.image_url}
                  className='h-full w-full object-cover'
                  muted
                  loop
                  playsInline
                  preload='metadata'
                  onMouseEnter={(e) =>
                    void e.currentTarget.play().catch(() => {})
                  }
                  onMouseLeave={(e) => e.currentTarget.pause()}
                />
                {isSelected && (
                  <CheckCircleIcon className='absolute left-1.5 top-1.5 h-5 w-5 text-indigo-600 drop-shadow dark:text-indigo-400' />
                )}
              </button>
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(video);
                }}
                className='absolute right-1.5 top-1.5 hidden rounded-full bg-black/50 p-1.5 transition-colors hover:bg-black/70 group-hover:block'
              >
                <TrashIcon className='h-3.5 w-3.5 text-white' />
              </button>
            </div>
          );
        })}
      </div>

      {isLoading && videos.length === 0 && (
        <div className='mt-4 flex justify-center'>
          <Spinner className='h-5 w-5 text-primary-500' />
        </div>
      )}

      <input
        ref={inputRef}
        type='file'
        accept='video/mp4,video/quicktime'
        className='hidden'
        onChange={handleUpload}
      />
    </div>
  );
}

function AudioTab({
  selected,
  previouslyApplied,
  maxSeconds,
  onChange,
}: {
  selected: ReferenceAudioValue | null;
  /** The parent page's currently applied audio — its object URL is owned by
   * the parent, so replacing it here must not revoke that URL. */
  previouslyApplied: ReferenceAudioValue | null;
  maxSeconds: number;
  onChange: (value: ReferenceAudioValue | null) => void;
}) {
  const { t } = useLocale('ai-images');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDecoding, setIsDecoding] = useState(false);

  const replaceSelection = (next: ReferenceAudioValue | null) => {
    // Compare by objectUrl: trim edits clone the value but keep the URL, and
    // the parent page owns the URL of the previously applied audio.
    if (selected && selected.objectUrl !== previouslyApplied?.objectUrl) {
      URL.revokeObjectURL(selected.objectUrl);
    }
    onChange(next);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again re-triggers onChange.
    event.target.value = '';
    if (!file) return;

    const validationError = validateReferenceAudioFile(file);
    if (validationError) {
      toast.error(t(validationError));
      return;
    }
    setIsDecoding(true);
    try {
      const decoded = await decodeReferenceAudioFile(file, maxSeconds);
      if (decoded.durationSec < 0.5) {
        URL.revokeObjectURL(decoded.objectUrl);
        toast.error(t('This audio file is too short.'));
        return;
      }
      replaceSelection(decoded);
    } catch {
      toast.error(t('Could not read this audio file. Try MP3 or WAV.'));
    } finally {
      setIsDecoding(false);
    }
  };

  return (
    <div>
      <div className='mb-4 pr-8'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          {t('Audio')}
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-slate-400'>
          {`${t(
            'Guide the soundtrack with speech, singing, or music',
          )} (${t('max')} ${maxSeconds}s). ${t(
            'Add at least one image reference to go with it.',
          )}`}
        </p>
      </div>

      {selected ? (
        <ReferenceAudioCard
          value={selected}
          maxSeconds={maxSeconds}
          onChange={onChange}
          onRemove={() => replaceSelection(null)}
        />
      ) : (
        <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            disabled={isDecoding}
            className='flex aspect-video flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-all hover:border-primary-400 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-primary-500 dark:hover:bg-slate-800'
          >
            {isDecoding ? (
              <Spinner className='h-6 w-6 text-primary-500' />
            ) : (
              <MusicNoteIcon className='h-6 w-6 text-gray-400 dark:text-slate-500' />
            )}
            <span className='text-sm font-medium text-gray-600 dark:text-slate-300'>
              {isDecoding ? t('Reading audio…') : t('Upload new')}
            </span>
            <span className='text-xs text-gray-400 dark:text-slate-500'>
              {t('MP3, WAV, M4A...')}
            </span>
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type='file'
        accept={ACCEPTED_AUDIO}
        className='hidden'
        onChange={handleUpload}
      />
    </div>
  );
}

export default memo(SceneModal);
